import type { ILogService, IWindowService } from "angular";
import { module } from "angular";
import type { IPoint } from "App/Modules/Models";
import type { ISvgService } from "./ISvgService";

export default class SvgService implements ISvgService {
    public static $inject = ["$log", "$window"];

    constructor(private readonly $log: ILogService, private readonly $window: IWindowService) {}

    public getSvgRoot(svgElement: SVGElement): SVGSVGElement {
        let svg = svgElement.parentNode;
        while (svg === null || svg === undefined || svg.nodeName !== "svg") {
            if (svg === undefined || svg.nodeName === "body") {
                // eslint-disable-next-line
                console.warn("SvgDirectives: Could not find SVG root");
                return null;
            }
            svg = svg.parentNode;
        }
        return svg as any as SVGSVGElement;
    }

    public pointToSvgCoordinates(svgRoot: SVGSVGElement, point: IPoint): SVGPoint {
        const p = svgRoot.createSVGPoint();
        p.x = 1.0 * point.x;
        p.y = 1.0 * point.y;

        const m = this.getScreenCTM(svgRoot);

        return p.matrixTransform(m.inverse());
    }

    public constrainToSvgBounds(svgRoot: SVGSVGElement, svgPoint: SVGPoint): void {
        const bBox = svgRoot.viewBox.baseVal;
        svgPoint.x = Math.max(svgPoint.x, bBox.x);
        svgPoint.x = Math.min(svgPoint.x, bBox.x + bBox.width);
        svgPoint.y = Math.max(svgPoint.y, bBox.y);
        svgPoint.y = Math.min(svgPoint.y, bBox.y + bBox.height);
    }

    public getSvgRectangle(svgRoot: SVGSVGElement): SVGRect {
        return svgRoot.viewBox.baseVal;
    }

    //  Following is from Holger Will since ASV3 and O9 do not support getScreenTCM()
    //  See http://groups.yahoo.com/group/svg-developers/message/50789
    public getScreenCTM(svgRoot: SVGSVGElement): SVGMatrix {
        if (svgRoot.getScreenCTM) {
            return svgRoot.getScreenCTM();
        }

        const root = svgRoot;
        let sCTM = root.createSVGMatrix();

        const tr = root.createSVGMatrix();
        let par = root.getAttribute("preserveAspectRatio");

        if (par === null || par === "") {
            // setting to default value
            par = "xMidYMid meet";
        }
        const parX = par.substring(0, 4); // xMin;xMid;xMax
        const parY = par.substring(4, 8); // YMin;YMid;YMax;
        const ma = par.split(" ");
        const mos = ma[1]; // meet;slice

        // get dimensions of the viewport
        sCTM.a = 1;
        sCTM.d = 1;
        sCTM.e = 0;
        sCTM.f = 0;

        let width = root.getAttribute("width");
        if (width === null || width === "") {
            width = this.$window.innerWidth.toString();
        }

        let height = root.getAttribute("height");
        if (height === null || height === "") {
            height = this.$window.innerHeight.toString();
        }

        let w: number;
        let h: number;

        //  Jeff Schiller:  Modified to account for percentages - I'm not
        //  absolutely certain this is correct but it works for 100%/100%
        if (width.substr(width.length - 1, 1) === "%") {
            w = (parseFloat(width.substr(0, width.length - 1)) / 100.0) * this.$window.innerWidth;
        } else {
            h = parseFloat(height);
        }

        if (height.substr(height.length - 1, 1) === "%") {
            h = (parseFloat(height.substr(0, height.length - 1)) / 100.0) * this.$window.innerHeight;
        } else {
            w = parseFloat(width);
        }

        //  get the ViewBox
        let vba = root.getAttribute("viewBox");
        if (vba == null) {
            vba = `0 0 ${w} ${h}`;
        }
        const vb = vba.split(" ").map((n) => parseInt(n, 10)); // get the viewBox into an array

        // --------------------------------------------------------------------------
        // create a matrix with current user transformation
        tr.a = root.currentScale;
        tr.d = root.currentScale;
        tr.e = root.currentTranslate.x;
        tr.f = root.currentTranslate.y;

        // scale factors
        const sx = w / vb[2];
        const sy = h / vb[3];

        let s: number;
        // meetOrSlice
        if (mos === "slice") {
            s = sx > sy ? sx : sy;
        } else {
            s = sx < sy ? sx : sy;
        }

        // preserveAspectRatio="none"
        if (par === "none") {
            sCTM.a = sx; // scaleX
            sCTM.d = sy; // scaleY
            sCTM.e = -vb[0] * sx; // translateX
            sCTM.f = -vb[0] * sy; // translateY
            sCTM = tr.multiply(sCTM); // taking user transformations into acount

            return sCTM;
        }

        sCTM.a = s; // scaleX
        sCTM.d = s; // scaleY
        // -------------------------------------------------------
        switch (parX) {
            case "xMid":
                sCTM.e = (w - vb[2] * s) / 2 - vb[0] * s; // translateX

                break;
            case "xMin":
                sCTM.e = -vb[0] * s; // translateX
                break;
            case "xMax":
                sCTM.e = w - vb[2] * s - vb[0] * s; // translateX
                break;
            default:
                break;
        }
        // ------------------------------------------------------------
        switch (parY) {
            case "YMid":
                sCTM.f = (h - vb[3] * s) / 2 - vb[1] * s; // translateY
                break;
            case "YMin":
                sCTM.f = -vb[1] * s; // translateY
                break;
            case "YMax":
                sCTM.f = h - vb[3] * s - vb[1] * s; // translateY
                break;
            default:
                break;
        }
        sCTM = tr.multiply(sCTM); // taking user transformations into acount

        return sCTM;
    }
}

module("mmGui").service("mmSvgService", SvgService);
